import React, { useEffect, useState } from "react";
import { RadioButton } from "primereact/radiobutton";
import { IKeyStatistics, IMyEntityAndGoals } from "../../../interface/myEntityAndGoals";
import UserProfiles from "./userProfiles";
import NotesAndGoals from "./notesAndGoals";
import GoalsInNumber from "./goalsInNumber";
import PropertiesAtGlance from "./propertiesAtGlance";
import { formatCurrency, formatNumberWithDecimal } from "../../../utils/commonUtil";

const EntityDetails: React.FC<{
  entity: IMyEntityAndGoals;
  handleRefreshData: () => void;
  viewOption: string;
}> = ({ entity, handleRefreshData, viewOption }) => {
  if (!entity) {
    return <div>No entity data available</div>;
  }
  const [entityData, setEntityData] = useState<IMyEntityAndGoals>(entity);
  const [statsOptions, setStatsOptions] = useState<string | "actuals" | "forecast">("actuals");
  const [filterKeyStats, setFilterKeyStats] = useState<IKeyStatistics>();

  useEffect(() => {
    setEntityData(entity);
    setFilterKeyStats(viewOption === "totals" ? entity.KeyStatisticsTotal : entity.KeyStatisticsPortion);
    setStatsOptions("actuals");
  }, [entity, viewOption]);

  const updateStatesData = (stats: string, view: string) => {
    if (stats === "forecast") {
      setFilterKeyStats((prev) =>
        view === "totals" ? entity.KeyStatisticsForecastTotal : entity.KeyStatisticsForecastPortion
      );
    } else {
      setFilterKeyStats((prev) => (view === "totals" ? entity.KeyStatisticsTotal : entity.KeyStatisticsPortion));
    }
  };

  const handleStatsOptionChange = (value: string) => {
    setStatsOptions((prev) => value);
    updateStatesData(value, viewOption);
  };

  const currencyTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatCurrency(-value)})` : formatCurrency(value)}
    </span>
  );

  const percentageTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatNumberWithDecimal(-value, 2, 2)}%)` : `${formatNumberWithDecimal(value, 2, 2)}%`}
    </span>
  );

  const renderKeyStatistics = () => {
    return (
      <div className="border-solid border border-gray-200">
        <div className="flex text-start justify-between pl-2 bg-slate-600 text-white">
          <h3>KEY STATISTICS</h3>
          <div className="flex flex-row content-center items-center gap-2">
            <div className="field-radiobutton mr-2">
              <RadioButton
                className="mx-2"
                inputId="actual"
                data-testid="megent-rdo-actuals"
                name="actual"
                value="actuals"
                onChange={(e) => {
                  handleStatsOptionChange(e.value);
                }}
                checked={statsOptions === "actuals"}
              />
              <label htmlFor="actual" className="ml-2 align-middle">
                Actuals
              </label>
            </div>
            <div className="field-radiobutton pr-2">
              <RadioButton
                inputId="forecast"
                data-testid="megent-rdo-forecast"
                name="forecast"
                value="forecast"
                onChange={(e) => {
                  handleStatsOptionChange(e.value);
                }}
                checked={statsOptions === "forecast"}
              />
              <label htmlFor="forecast" className="ml-2 align-middle">
                Forecast
              </label>
            </div>
          </div>
        </div>

        <div className="border-solid border border-gray-200 p-2 ">
          <div className="flex flex-row content-center justify-evenly font-bold text-xl">
            <div className="w-[11.1%]">{filterKeyStats?.noOfProperty}</div>
            <div className="w-[11.1%]">{currencyTemplate(filterKeyStats?.CurrentMarketValue)}</div>
            <div className="w-[11.1%]">{currencyTemplate(filterKeyStats?.NetEquity)}</div>
            <div className="w-[11.1%]">{currencyTemplate(filterKeyStats?.PreTaxCashflow)}</div>
            <div className="w-[11.1%]">{currencyTemplate(filterKeyStats?.PostTaxCashflow)}</div>
            <div className="w-[11.1%]">{currencyTemplate(filterKeyStats?.CapitalGrowth)}</div>
            <div className="w-[11.1%]">{percentageTemplate(filterKeyStats?.CapitalGrowthPercentage)}</div>
            <div className="w-[11.1%]">{percentageTemplate(filterKeyStats?.LVR)}</div>
            <div className="w-[11.1%]">
              {filterKeyStats?.DSR != null
                ? filterKeyStats.DSR < 0
                  ? `(${formatNumberWithDecimal(filterKeyStats.DSR, 2, 2)})`
                  : formatNumberWithDecimal(filterKeyStats.DSR, 2, 2)
                : ""}
            </div>
          </div>
          <div className="flex flex-row content-center mt-2 justify-evenly">
            {[
              "Properties held",
              "Current market value",
              "Net equity",
              "Pre-tax cash flow YTD",
              "After-tax cash flow YTD",
              "Capital growth",
              "Total Capital growth",
              "LVR",
              "DSR",
            ].map((label, index) => (
              <div key={index} className="w-[11.1%]">
                {label}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const RefreshData = () => {
    handleRefreshData();
  };

  const TransformData = () => {
    const data = entityData.Properties.map((property) => {
      const propertyData = viewOption == "totals" ? property.PropertyKeyValuesTotal : property.PropertyKeyValuesPortion;

      const address = [property.StreetAddress, property.Suburb, property.District, property.State, property.Postcode]
        .filter(Boolean)
        .join(", ");

      const addressWithCountry = `${address}${property.Country ? ` (${property.Country})` : ""}`;
      return {
        name: addressWithCountry,
        value: propertyData.CurrentMarketValue,
        netEquity: propertyData.NetEquity,
        lvr: propertyData.LVR,
        dsr: propertyData.DSR,
        capitalGrowth: propertyData.CapitalGrowth,
        growth: propertyData.CapitalGrowthPercentage,
        propertyId: property.PortfolioTrackerId,
      };
    });
    return data;
  };

  const rearrangeGoals = (goals: any) => {
    if (!goals) return goals;
    const { Forecast, Actual, ...rest } = goals;
    return {
      ...(Forecast && { Forecast }),
      ...(Actual && { Actual }),
      ...rest,
    };
  };

  entityData.GoalsTotal = rearrangeGoals(entityData.GoalsTotal);
  entityData.GoalsPortion = rearrangeGoals(entityData.GoalsPortion);

  return (
    <div className="flex flex-col p-4">
      {entity.EntityId != "" && entity.EntityTypeName != "Personal Ownership" && (
        <div>
          <UserProfiles handleRefreshData={RefreshData} profiles={entityData} entityId={entity.EntityId}></UserProfiles>
        </div>
      )}
      <div className="mt-2">{entity && renderKeyStatistics()}</div>
      {entity.EntityId != "" && (
        <div className="mt-2">
          <NotesAndGoals isNote={false} entityData={entityData}></NotesAndGoals>
        </div>
      )}

      <div className="mt-2">
        <GoalsInNumber
          goalsData={entityData}
          viewOption={viewOption == "totals" ? true : false}
          refreshData={RefreshData}
        ></GoalsInNumber>
      </div>
      <div className="mt-2">
        <PropertiesAtGlance properties={TransformData()}></PropertiesAtGlance>
      </div>
      <div className="text-start m-2 italic text-blue-400">
        To add, remove, or edit properties in this entity, click on the property address to modify within the property
        details screen
      </div>
      {entity.EntityId != "" && (
        <div className="mt-2">
          <NotesAndGoals isNote={true} entityData={entityData}></NotesAndGoals>
        </div>
      )}
    </div>
  );
};

export default EntityDetails;
